/**
 * L'onglet « Séquences » de la fiche d'un élève — chantier « manuel vivant »,
 * Φ-MV-5 (MV-TUT-06), devenu le SUIVI DE SES PARCOURS au lot P3 (AB#975,
 * épopée AB#961). Ce que le tuteur a déposé, et CE QUE L'ÉLÈVE EN A FAIT :
 * « 3 sur 7 », en retard ou non, et chaque étape « Fait le … » ou « À faire ».
 * C'est le serveur qui coche (un exercice corrigé, une fiche ouverte, une
 * capsule regardée jusqu'au bout) : l'écran l'affiche tel quel.
 *
 * ─── Deux sources, une forme ────────────────────────────────────────────────
 *
 * La route des parcours du tuteur (`GET …/eleves/:id/parcours`, P1). Tant
 * qu'elle répond 404, les séquences d'avant (`GET …/sequences`) s'affichent
 * COMME DES PARCOURS DÉPOSÉS PAR LE TUTEUR : un seul bloc, le même « 3 sur 7 »
 * (`suiviDeSequence`). Le même numéro partout : chaque étape porte son rang
 * global, jamais « Étape 4 sur 7 » (fondateur, 19 oct. 2026, AB#961). Pas de
 * rappel : le tuteur n'en envoie pas (même décision).
 *
 * ─── La charge (F-11) ───────────────────────────────────────────────────────
 *
 * Un parcours = une section rabattable. Le premier en cours est ouvert, les
 * autres repliés ; les parcours retirés restent consultables, repliés —
 * retirer n'est pas supprimer, les travaux faits restent au dossier.
 *
 * ─── Retirer ────────────────────────────────────────────────────────────────
 *
 * Une confirmation EN FENÊTRE, qui dit ce qui est retiré et ce qui reste. Seul
 * le tuteur qui a déposé le parcours le modifie ou le retire ; la retouche
 * passe encore par la route des séquences, à plat (`ComposerSequencePage`).
 */
import { denombrer } from '@silex/shared';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { Fenetre } from '../../components/Fenetre';
import { SectionRabattable } from '../../components/SectionRabattable';
import { TexteMaths } from '../../components/TexteMaths';
import {
  LIBELLES_ETAT_PARCOURS,
  adressesManuelEleve,
  adressesParcours,
  aujourdhuiConsole,
  faitesSurTotal,
  jourCivil,
  suiviDeParcours,
  suiviDeSequence,
  type ParcoursSuivi,
} from '../../lib/manuel-tuteur-format';
import type { BadgeTone } from '../../lib/suivi-format';
import { formatDate } from '../../lib/tuteur-format';
import { messageErreur, useAsyncRechargeable } from '../../lib/useAsync';

const TON_ETAT: Readonly<Record<ParcoursSuivi['etat'], BadgeTone>> = {
  en_cours: 'en-cours',
  en_pause: 'a-revoir',
  termine: 'acquis',
  archive: 'a-revoir',
};

/**
 * Les parcours de l'élève, vus par le tuteur. Un 404 de la route des parcours
 * veut dire « P1 pas encore servi » : on lit les séquences, comme avant.
 */
export async function lireParcoursSuivis(
  eleveId: string,
  aujourdhui: string,
): Promise<readonly ParcoursSuivi[]> {
  try {
    const parcours = await api.manuelParcoursEleve(eleveId);
    return parcours.map((p) => suiviDeParcours(p, aujourdhui));
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err;
    const { sequences } = await api.manuelSequences(eleveId);
    return sequences.map((s) => suiviDeSequence(s, aujourdhui));
  }
}

/** « En pause depuis le 17 octobre », « En cours ». */
export function direEtat(p: ParcoursSuivi): string {
  if (p.etat === 'en_pause' && p.pauseLe !== null) {
    return `En pause depuis le ${formatDate(p.pauseLe)}`;
  }
  return LIBELLES_ETAT_PARCOURS[p.etat];
}

/** « Déposé le 8 octobre par Karim · pour le 14 octobre ». */
function direDepot(p: ParcoursSuivi): string {
  return [
    `Déposé le ${formatDate(p.creeLe)}${p.deLaPartDe === null ? '' : ` par ${p.deLaPartDe}`}`,
    p.pourLe === null ? 'sans date butoir' : `pour le ${jourCivil(p.pourLe)}`,
  ].join(' · ');
}

/** Le résumé d'une section repliée : LE nombre, l'état, le retard. */
export function resumeParcours(p: ParcoursSuivi): string {
  return [faitesSurTotal(p.faites, p.total), direEtat(p), p.enRetard ? 'En retard' : null]
    .filter((m): m is string => m !== null)
    .join(' · ');
}

export function SequencesEleve({
  eleveId,
  prenom,
  peutEcrire,
  surChangement,
}: {
  readonly eleveId: string;
  readonly prenom: string;
  readonly peutEcrire: boolean;
  /** Un parcours retiré change les comptes de l'en-tête : la fiche se relit. */
  readonly surChangement: () => void;
}) {
  const { resource: liste, recharger } = useAsyncRechargeable(
    useMemo(() => () => lireParcoursSuivis(eleveId, aujourdhuiConsole()), [eleveId]),
  );
  const [aRetirer, setARetirer] = useState<ParcoursSuivi | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const retirer = async (): Promise<void> => {
    if (aRetirer === null) return;
    setEnvoi(true);
    setRefus(null);
    try {
      await api.manuelRetirerSequence(aRetirer.id);
      setARetirer(null);
      recharger();
      surChangement();
    } catch (err) {
      setRefus(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  if (liste.status === 'loading') return <Chargement quoi="ses parcours" />;
  if (liste.status === 'error') return <ErreurRessource message={liste.message} />;

  return (
    <ListeDesParcours
      parcours={liste.data}
      eleveId={eleveId}
      prenom={prenom}
      peutEcrire={peutEcrire}
      surRetrait={(p) => {
        setRefus(null);
        setARetirer(p);
      }}
    >
      {aRetirer !== null ? (
        <Fenetre
          titre={`Retirer le parcours de la liste de ${prenom} ?`}
          sous={`« ${aRetirer.nom} » — ${resumeParcours(aRetirer)}.`}
          testId="sequence-retrait-fenetre"
          surFermeture={() => setARetirer(null)}
          actions={
            <>
              <button type="button" className="btn btn--ghost" onClick={() => setARetirer(null)}>
                Garder le parcours
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={envoi}
                onClick={() => void retirer()}
                data-testid="sequence-retrait-confirmer"
              >
                Retirer le parcours
              </button>
            </>
          }
        >
          <p className="note-line">Les travaux déjà faits restent au dossier.</p>
          {refus !== null ? (
            <div className="callout callout--rare" role="alert">
              <strong>Parcours non retiré.</strong> {refus}
            </div>
          ) : null}
        </Fenetre>
      ) : null}
    </ListeDesParcours>
  );
}

/** La liste, une fois lue : son rendu s'éprouve sans serveur. */
export function ListeDesParcours({
  parcours,
  eleveId,
  prenom,
  peutEcrire,
  surRetrait,
  children,
}: {
  readonly parcours: readonly ParcoursSuivi[];
  readonly eleveId: string;
  readonly prenom: string;
  readonly peutEcrire: boolean;
  readonly surRetrait: (p: ParcoursSuivi) => void;
  readonly children?: ReactNode;
}) {
  const enListe = parcours.filter((p) => p.etat !== 'archive');
  const retires = parcours.filter((p) => p.etat === 'archive');
  const premierEnCours = enListe.find((p) => p.etat === 'en_cours');

  return (
    <div className="stack" data-testid="manuel-sequences">
      {peutEcrire ? (
        <p className="row">
          <Link
            className="btn btn--ghost btn--sm"
            to={adressesParcours.composer({ eleve: eleveId })}
            data-testid="manuel-nouveau-parcours"
          >
            Nouveau parcours
          </Link>
        </p>
      ) : null}

      {parcours.length === 0 ? (
        <p className="note-line" data-testid="manuel-aucun-parcours">
          Aucun parcours.
        </p>
      ) : null}

      {enListe.map((p) => (
        <div key={p.id} data-testid="manuel-sequence">
          <SectionRabattable
            titre={p.nom}
            resume={resumeParcours(p)}
            sous={direDepot(p)}
            ouverteParDefaut={p.id === premierEnCours?.id}
            actions={
              peutEcrire && (p.peutModifier || p.peutRetirer) ? (
                <>
                  {p.peutModifier ? (
                    <Link
                      className="btn btn--ghost btn--sm"
                      to={adressesManuelEleve.composer(eleveId, p.id)}
                    >
                      Modifier
                    </Link>
                  ) : null}
                  {p.peutRetirer ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => surRetrait(p)}
                      data-testid="manuel-sequence-retirer"
                    >
                      Retirer
                    </button>
                  ) : null}
                </>
              ) : null
            }
          >
            <Parcours parcours={p} prenom={prenom} />
          </SectionRabattable>
        </div>
      ))}

      {retires.length > 0 ? (
        <SectionRabattable
          titre="Parcours retirés"
          resume={`${denombrer(retires.length, 'retiré')} · les travaux faits restent au dossier`}
          ouverteParDefaut={false}
          cleMemoire="manuel.sequences.retirees"
        >
          <div className="stack-sm">
            {retires.map((p) => (
              <div key={p.id} className="preparation" data-testid="manuel-sequence-retiree">
                <strong>{p.nom}</strong>
                <span className="cell-sub">{direDepot(p)}</span>
                <Parcours parcours={p} prenom={prenom} />
              </div>
            ))}
          </div>
        </SectionRabattable>
      ) : null}

      {children}
    </div>
  );
}

/** Un parcours, déplié : son état, son mot, ses blocs et ce que l'élève en a fait. */
function Parcours({
  parcours: p,
  prenom,
}: {
  readonly parcours: ParcoursSuivi;
  readonly prenom: string;
}) {
  return (
    <>
      <p className="row wrap">
        <strong data-testid="parcours-faites">{faitesSurTotal(p.faites, p.total)}</strong>
        <Badge tone={TON_ETAT[p.etat]} label={direEtat(p)} />
        {p.enRetard ? <Badge tone="a-revoir" label="En retard" /> : null}
      </p>
      {p.mot === null ? null : (
        <p className="sequence-mot">
          <span className="lbl">Mot à {prenom}</span>
          {p.mot}
        </p>
      )}
      {p.blocs.map((bloc, i) => (
        <div key={`${i}-${bloc.titre}`} className="stack-sm" data-testid="parcours-suivi-bloc">
          {p.blocs.length > 1 ? <h4>{bloc.titre}</h4> : null}
          <ol className="sequence-elements">
            {bloc.etapes.map((e) => (
              <li key={e.id} className="sequence-element" data-testid="manuel-sequence-element">
                <span className="sequence-element__rang" aria-hidden="true">
                  {e.rang}
                </span>
                <span className="sequence-element__corps">
                  <span className="cell-sub">
                    {e.nomNiveau === null ? e.libelleGenre : `${e.libelleGenre} · ${e.nomNiveau}`} ·
                    environ {e.minutes} min
                  </span>
                  <TexteMaths texte={e.titre} />
                  {/* La route des parcours ne sert pas la raison ; une séquence, si
                      (`null` : « Ajouté par le tuteur », comme avant). */}
                  {e.raison === undefined ? null : (
                    <span className="cell-sub">{e.raison ?? 'Ajouté par le tuteur'}</span>
                  )}
                  {e.servable ? null : (
                    <span className="cell-sub">
                      Cette ressource n’est plus en service : elle n’est plus proposée.
                    </span>
                  )}
                </span>
                <Badge
                  tone={e.fait ? 'acquis' : 'en-cours'}
                  label={
                    e.fait && e.faitLe !== null ? `Fait le ${formatDate(e.faitLe)}` : 'À faire'
                  }
                />
              </li>
            ))}
          </ol>
        </div>
      ))}
    </>
  );
}
